import { useDroppable } from '@dnd-kit/core';
import React from 'react';
import { ExternalEventsList } from 'shared/components/ui/external-events-list';
import { TaskCardTasksList } from 'shared/components/ui/task-card-tasks-list';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { ExternalCalendarEvent } from 'shared/types/external-calendar-event';

import * as Styled from './task-card-task-section.style';
import { TaskCardTaskSectionLayout } from './task-card-task-section-layout';

export type TaskCardTaskSectionProps = Omit<
  React.ComponentProps<typeof TaskCardTaskSectionLayout>,
  'children'
> &
  Omit<React.ComponentProps<typeof TaskCardTasksList>, 'canDragTasks'> & {
    droppableId?: string;
    canReorder?: boolean;
    externalEvents?: ExternalCalendarEvent[];
    timeFormat: TimeFormatOptions;
  };

export const TaskCardTaskSection: React.FC<TaskCardTaskSectionProps> = ({
  goalId,
  date,
  displayDateRange,
  startTime,
  duration,
  priority,
  tagIds,
  tasks,
  selectedTask,
  showRepeatingAsTodo,
  showRepeatingAsDoneOnDateEntry,
  displayDue,
  onTask,
  onUnfocus,
  canCreateTask,
  goals,
  lifeAreas,
  customHeader,
  headerToDisplay,
  headerIsLink,
  hideGoal,
  hideMeta,
  parentChildIds,
  parentIds,
  onHeader,
  canCollapse,
  onPremium,
  droppableId,
  canReorder,
  externalEvents,
  timeFormat,
}) => {
  const { setNodeRef } = useDroppable({
    id: droppableId ?? '',
    disabled: !droppableId || !!tasks.length,
  });

  const containerProps = droppableId ? { ref: setNodeRef } : {};

  return (
    <Styled.Container {...containerProps}>
      <TaskCardTaskSectionLayout
        goalId={goalId}
        canCreateTask={canCreateTask}
        goals={goals}
        lifeAreas={lifeAreas}
        date={date}
        startTime={startTime}
        duration={duration}
        priority={priority}
        tagIds={tagIds}
        headerToDisplay={headerToDisplay}
        onHeader={onHeader}
        customHeader={customHeader}
        canCollapse={canCollapse}
        parentIds={parentIds}
        parentChildIds={parentChildIds}
        hideMeta={hideMeta}
        headerIsLink={headerIsLink}
        onPremium={onPremium}
      >
        {!!externalEvents?.length && (
          <Styled.ExternalEventsListContainer>
            <ExternalEventsList
              events={externalEvents}
              timeFormat={timeFormat}
              show
            />
          </Styled.ExternalEventsListContainer>
        )}
        <TaskCardTasksList
          tasks={tasks}
          displayDate={date}
          displayDateRange={displayDateRange}
          onTask={onTask}
          onUnfocus={onUnfocus}
          selectedTask={selectedTask}
          showRepeatingAsTodo={showRepeatingAsTodo}
          showRepeatingAsDoneOnDateEntry={showRepeatingAsDoneOnDateEntry}
          displayDue={displayDue}
          canReorder={canReorder}
          canDragTasks={!!droppableId}
          hideGoal={hideGoal}
        />
      </TaskCardTaskSectionLayout>
    </Styled.Container>
  );
};
